import { z } from 'zod';
import { sectionHasValue } from './util';
import { baseBloodPressureRefined } from './oasis-sections/vitals';
import { basePainRefined } from './shared/pain';
import { baseMusculoskeletalRefined } from './oasis-sections/section-g';
import { baseGenitourinarySchema } from './shared/genitourinary';
import { basePsychosocialSchemaRefined } from './shared/psychosocial';
import { baseNeurologicalSchemaRefined } from './shared/neurological';
import { baseIntegumentaryRefined } from './shared/integumentary';
import { baseCardiovascularSchemaRefined } from './shared/cardiovascular';
import { baseGastrointestinalSchema } from './shared/gastrointestinal';
import { baseLungAuscultationSchemaRefined, baseBreathingPatternSchema, baseCpapBipapSchema, baseMechanicalVentilatorSchema, baseTracheostomySchema, baseRespiratoryTreatmentsSchema, baseSmokingHistorySchema, baseCoughSchema, } from './shared/respiratory';
import { baseTemperatureRefined, baseO2Refined, baseRespirationRefined, basePulseRefined, baseBloodSugarRefined } from './shared/vitals';
import { baseEndocrineSchema } from './shared/endocrine';
import { baseHealthManagementSchema } from './shared/health-management';
import { baseOstomySchemaRefined } from './shared/ostomy';
import { basePainAssessmentSchema } from './shared/pain-assessment';
import { baseLabDrawSchema } from './shared/labDraw';
import { baseOralIntakeClinical, baseEatingSupport, basePocNutrition, baseK0520Clinical, baseWeightClinicalRefined, } from './shared/nutrition';
export const snnValidationSchema = z.object({
    id: z.string().optional(),
    data: z.object({
        SubjectiveReport: z
            .object({
            chiefComplaint: z.string().optional(),
            changeStatus: z.string().optional(),
            changeDetails: z.string().optional(),
            primaryConditionStatus: z.string().optional(),
            patientComplianceStatus: z.string().optional(),
            patientPresentDuringVisit: z.string().default('yes'),
            caregiverPresent: z.string().optional(),
        })
            .superRefine((data, ctx) => {
            if ((data.changeStatus === 'worse' || data.changeStatus === 'emergency-visit') && !data.changeDetails?.trim()) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'required',
                    path: ['changeDetails'],
                });
            }
        }),
        ActiveDiagnoses: z.boolean().nullish(),
        HomeboundReason: z
            .object({
            homeboundStatusThisVisit: z.string().min(1, 'required'),
            whatChanged: z.array(z.string()).optional(),
            dateChangeNoticed: z.string().optional(),
            triggerEvent: z.string().optional(),
            changeDescription: z.string().optional(),
            lastDayMetCriteria: z.string().optional(),
            reasonNoLongerHomebound: z.string().optional(),
            clinicalJustification: z.string().optional(),
            activeQualifyingConditions: z.array(z.string()).optional(),
            activeQualifyingConditionsOther: z.string().optional(),
            activeMobilityStatus: z.array(z.string()).optional(),
        })
            .superRefine((data, ctx) => {
            if (data.homeboundStatusThisVisit === 'changed' && !data.changeDescription?.trim()) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'required',
                    path: ['changeDescription'],
                });
            }
        }),
        Vitals: z.object({
            bloodPressure: baseBloodPressureRefined,
            pulse: basePulseRefined,
            respiration: baseRespirationRefined,
            temperature: baseTemperatureRefined,
            o2: baseO2Refined,
            pain: basePainRefined,
            bloodSugar: baseBloodSugarRefined,
        }),
        Nutrition: z.object({
            weightClinical: baseWeightClinicalRefined,
            oralIntakeClinical: baseOralIntakeClinical.refine((data) => sectionHasValue(data), {
                message: 'Mark as within normal limits if none applies',
                path: [],
            }),
            eatingSupport: baseEatingSupport.refine((data) => sectionHasValue(data), {
                message: 'Mark as within normal limits if none applies',
                path: [],
            }),
            pocNutrition: basePocNutrition,
            nutritionalApproaches: z.array(z.string()).optional(),
            nutritionalApproachesClinical: baseK0520Clinical.optional(),
        }),
        Neurosensory: baseNeurologicalSchemaRefined.extend({
            painAssessment: basePainAssessmentSchema,
            psychosocial: basePsychosocialSchemaRefined,
        }),
        Cardiovascular: z.object({
            cardiovascular: baseCardiovascularSchemaRefined,
        }),
        Respiratory: z.object({
            smokingHistory: baseSmokingHistorySchema,
            breathingPattern: baseBreathingPatternSchema,
            lungAuscultation: baseLungAuscultationSchemaRefined,
            cough: baseCoughSchema,
            cpapBipap: baseCpapBipapSchema,
            mechanicalVentilator: baseMechanicalVentilatorSchema,
            tracheostomy: baseTracheostomySchema,
            treatments: baseRespiratoryTreatmentsSchema,
        }),
        Endocrine: z.object({
            endocrine: baseEndocrineSchema.extend({ changeFromLastVisit: z.string().min(1, 'required') }),
        }),
        BladderBowel: z.object({
            gastrointestinal: baseGastrointestinalSchema,
            genitourinary: baseGenitourinarySchema,
        }),
        Musculoskeletal: z.object({
            musculoskeletal: baseMusculoskeletalRefined,
        }),
        HealthManagement: z.object({
            healthManagement: baseHealthManagementSchema,
        }),
        SkinConditions: z.object({
            integumentary: baseIntegumentaryRefined,
        }),
        Ostomy: z.object({
            ostomy: baseOstomySchemaRefined,
        }),
        Labdraw: z.object({
            labdraw: baseLabDrawSchema,
        }),
        Infections: z.boolean().nullish(),
        Incidents: z.boolean().nullish(),
        Medication: z.boolean().nullish(),
        Supplies: z.boolean().nullish(),
        PlanOfCare: z.boolean().refine((value) => !!value, { message: 'section has errors' }),
        CoordinationOfCare: z.object({
            physicianNotified: z.array(z.string()).optional(),
            nextVisitPlans: z.string().optional(),
            coordinationDiscipline: z.array(z.string()).optional(),
            coordinationName: z
                .object({
                md: z.string().optional(),
                np: z.string().optional(),
                pa: z.string().optional(),
                sn: z.string().optional(),
                lpn: z.string().optional(),
                pt: z.string().optional(),
                ot: z.string().optional(),
                slp: z.string().optional(),
                hha: z.string().optional(),
            })
                .optional(),
            narrative: z.string().optional(),
        }),
    }),
    signedUserId: z.string().min(1, 'required'),
    signedUserDate: z.string().min(1, 'required'),
});
